import React from 'react';
import { Link } from 'react-router-dom';

import RecordBadge from '../common/RecordBadge';
import { describeTagInUse, describeTagLookups, tagRefusal } from '../../utils/tags';
import { EXTERNAL_LINK_PROPS } from '../common/ExternalLink';

/**
 * A TAG WRITE'S REFUSAL, as a person reads it (utils/tags.js tagRefusal) —
 * and, when ACCESS SETTINGS name the tag (409 `tag_in_use`, `reason:
 * 'access'`; T7), what uses it: each setting the person manages by name, a
 * link to where it is changed (the collection's Sharing tab; a link or a link
 * request, its Linked collections section), and how many more they can't
 * see, never named:
 *
 *   “Board only” decides who can see what. It’s used by Alex Rivera’s access
 *   level (Minutes), the public link “Agenda” (Minutes), and 2 more you can’t
 *   see. Change them first.
 *
 * When STRICT LOOKUPS name it instead (`reason: 'lookup'`, b80), the
 * lookups are said as their cards' and element sets' badges
 * (TagLookupsRefusal, below).
 *
 * ⚑ Each link opens in a new tab, as the impact report's do: the decision in
 * hand stays open. Rendered where Settings › Tags says a refusal — the tag
 * manager's line (a delete, a move) and the tag dialog (Restricted off).
 *
 *   error     the axios error
 *   lead      a sentence before it ("“Board only” was not deleted.") — the
 *             tag is then "It"; without one the refusal names `tagName`
 *   tagName   the tag's name, for a refusal said on its own
 *   fallback, tags   as tagRefusal takes them
 */
export default function TagRefusal({ error, lead = null, tagName = null, fallback, tags = null }) {
	const inUse = describeTagInUse(error, { tagName: lead ? null : tagName });
	const lookups = inUse ? null : describeTagLookups(error);
	if (lookups) return <TagLookupsRefusal lead={lead} parts={lookups} />;
	if (!inUse) {
		const text = tagRefusal(error, fallback, { tags, tagName: lead ? null : tagName });
		return <>{lead ? `${lead} ${text}` : text}</>;
	}
	return (
		<span className="tag-in-use" data-testid="tag-in-use">
			{lead ? `${lead} ` : ''}
			{inUse.lead}
			{' It’s used by '}
			<TagSettingsList references={inUse.references} more={inUse.more} />
			{'. '}
			{inUse.tail}
		</span>
	);
}

/**
 * STRICT LOOKUPS THAT NAME THE TAG (409 `tag_in_use`, `reason: 'lookup'`;
 * the owner's ruling b80): a lookup that only offers items with the tag
 * would refuse every new value once it is gone, so the delete waits until
 * its filter is edited. Each lookup the person can open is the record badge
 * of its card or element set — the door to where the filter is edited, in a
 * new tab as the impact report's links — and the rest are counted, never
 * named:
 *
 *   This tag is used by 2 lookups that only offer items with it. Edit their
 *   filters first: [Job] and 1 lookup you cannot open.
 */
function TagLookupsRefusal({ lead, parts }) {
	const items = parts.references.map(ref => (
		<RecordBadge
			key={ref.key}
			kind={ref.kind}
			record={ref.record}
			title={ref.title}
			size="xs"
			to={ref.to}
			{...EXTERNAL_LINK_PROPS}
			collection={ref.collection}
			className="tag-in-use__lookup"
		/>
	));
	return (
		<span className="tag-in-use tag-in-use--lookups" data-testid="tag-in-use">
			{lead ? `${lead} ` : ''}
			{parts.lead}
			{' '}
			{parts.references.length ? (
				<>
					{parts.tail}
					{' '}
					<span className="tag-in-use__lookups">
						{items.flatMap((badge, index) => (index === 0 ? [badge] : [
							<React.Fragment key={`sep-${index}`}>{!parts.more && index === items.length - 1 ? ' and ' : ', '}</React.Fragment>,
							badge,
						]))}
						{parts.more ? `${items.length > 1 ? ',' : ''} and ${parts.more}` : ''}
					</span>
					.
				</>
			) : `${parts.more} ${parts.tail}`}
		</span>
	);
}

/**
 * ACCESS SETTINGS, NAMED, in a sentence (utils/tags.js tagHolderReference's
 * parts): each one the person manages as a link to where it is changed (a
 * new tab), with where it lives, then `more` — "and 2 more you can’t see".
 * `tag_in_use` says them this way, and so does the warning "Apply to new
 * items of" gives before it widens (b75, confirmNewItems.js).
 */
export function TagSettingsList({ references = [], more = null }) {
	const parts = references.map(ref => (
		<React.Fragment key={ref.key}>
			{ref.to ? (
				<Link to={ref.to} {...EXTERNAL_LINK_PROPS} className="tag-in-use__link">{ref.text}</Link>
			) : ref.text}
			{ref.where ? ` (${ref.where})` : ''}
		</React.Fragment>
	));
	if (more) parts.push(<React.Fragment key="more">{more}</React.Fragment>);
	return (
		<>
			{parts.flatMap((part, index) => {
				if (index === 0) return [part];
				const last = index === parts.length - 1;
				const separator = parts.length === 2 ? ' and ' : (last ? ', and ' : ', ');
				return [<React.Fragment key={`sep-${index}`}>{separator}</React.Fragment>, part];
			})}
		</>
	);
}
